/**
 * Which search profiles a tender hit — in the Trefferliste and on the Board.
 *
 * A tender that hits several profiles is seen by every department behind them (roles have no
 * hierarchy), so it is marked „bereichsübergreifend“: otherwise two departments work on it without
 * either knowing about the other.
 *
 * `matchedProfiles` spans every customer (tenders are shared documents, tri-server
 * docs/tender-board.md), so names are looked up among the customer's **own** profiles and an id
 * without a name is dropped — counting the raw array would mark nearly every tender.
 */
import { useTranslation } from "react-i18next";

/** Names of the customer's own profiles among `profileIds`, deduplicated, in the given order. */
export const matchedProfileNames = (
  profileIds: readonly string[] | undefined,
  nameById: ReadonlyMap<string, string>,
): string[] => {
  const names: string[] = [];
  for (const id of profileIds ?? []) {
    const name = nameById.get(id);
    if (name && !names.includes(name)) names.push(name);
  }
  return names;
};

/** Two or more own profiles — `multiProfile` is the server's own count, for when names are missing. */
export const isCrossProfile = (names: readonly string[], multiProfile = false): boolean =>
  names.length > 1 || multiProfile;

export function TenderProfileBadges({
  names,
  multiProfile = false,
  stacked = false,
}: {
  names: readonly string[];
  /** The server's flag; keeps the marker visible while the profile names are not loaded. */
  multiProfile?: boolean;
  /** One chip per line (narrow table column) instead of wrapping inline. */
  stacked?: boolean;
}) {
  const { t } = useTranslation();
  const cross = isCrossProfile(names, multiProfile);
  if (!names.length && !cross) return null;
  return (
    <span className={`inline-flex gap-1 ${stacked ? "flex-col items-start" : "flex-wrap items-center"}`}>
      {cross && (
        <span
          className="rounded border border-amber-600 bg-amber-50 px-1 py-px text-[10px] font-semibold text-amber-800"
          title={
            names.length
              ? t("tenderBoard.profiles.crossHint", { count: names.length, names: names.join(", ") })
              : undefined
          }
        >
          {t("tenderBoard.profiles.cross")}
        </span>
      )}
      {names.map((name) => (
        <span
          key={name}
          className={`rounded border border-border bg-muted px-1 py-px text-[10px] text-foreground ${
            stacked ? "max-w-full break-words" : "max-w-[320px] truncate"
          }`}
          title={t("tenderBoard.profiles.chipHint", { name })}
        >
          {name}
        </span>
      ))}
    </span>
  );
}
